import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  Divider,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Close as XIcon,
  SwapHoriz as ReassignIcon,
} from '@mui/icons-material';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import { formatRelativeTime } from '../helpers/dateUtils';
import ApprovalActionPreview, { ApprovalActionPreviewItem } from './ApprovalActionPreview';

interface ApprovalPanelProps {
  open: boolean;
  onClose: () => void;
  channelId?: string;
  threadId?: string;
  messageId?: string;
  approvals?: Array<{
    id: string;
    approval_details: {
      status: string;
      approver: {
        id: string;
        name: string;
        email_id: string;
        avatar_url?: string;
        username: string;
      };
      requested_by: {
        id: string;
        name: string;
        email_id: string;
        avatar_url?: string;
        username: string;
      };
      title: string;
      description: string;
      approval_notes?: string;
      rejection_reason?: string;
      comment?: string;
      created_at: string;
      updated_at: string;
      approved_at?: string;
      rejected_at?: string;
      approval_metadata: any;
      /** What approving will run (deferred actions), shaped for display by base_shay. */
      action_preview?: ApprovalActionPreviewItem[] | null;
    };
    approval_history: Array<{
      action: string;
      details: string;
      timestamp: string;
      user: {
        id: string;
        name: string;
        email_id: string;
        avatar_url?: string;
        username: string;
      };
      metadata: any;
    }>;
    context: any;
  }>;
  onApprovalUpdated?: () => void;
  /** When true, parent is still fetching approvals; show loading instead of "no data" */
  approvalsLoading?: boolean;
}

interface ApprovalStatus {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'reassigned';
  approver?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  assignedBy: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  assignedAt: string;
  message?: string;
  actionPreview?: ApprovalActionPreviewItem[];
}

/** Heading for the action preview; undefined keeps the component's default ("What you're approving"). */
const previewHeadingFor = (status: ApprovalStatus['status']): string | undefined => {
  if (status === 'approved') return 'What was approved';
  if (status === 'rejected') return 'What was rejected';
  return undefined;
};

const ApprovalPanel: React.FC<ApprovalPanelProps> = ({
  open, 
  onClose, 
  channelId,
  threadId,
  messageId,
  approvals,
  onApprovalUpdated,
  approvalsLoading = false,
}) => {
  // Theme for dark mode support (panel background, borders, text)
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<'approve' | 'reject' | 'reassign' | null>(null);
  const [comment, setComment] = useState('');
  const [selectedApprover, setSelectedApprover] = useState('');
  const [members, setMembers] = useState<Array<{user_id: string, name: string, email_id: string, user_role: string, membership_role: string}>>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Function to check if current user is the assigned approver
  const isCurrentUserApprover = () => {
    if (!approvals || approvals.length === 0) return false;
    
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (!storedUserData) return false;
      
      const parsedUserData = JSON.parse(storedUserData);
      const currentUserId = parsedUserData.user_id;
      
      // Always use 0th index for approval details
      const approval = approvals[0];
      return currentUserId === approval.approval_details.approver.id;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  };

  // Function to check if current user is admin
  const isCurrentUserAdmin = () => {
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (!storedUserData) return false;
      
      const parsedUserData = JSON.parse(storedUserData);
      return parsedUserData.role === 'admin';
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  };

  // Function to check if current user is channel admin
  const isCurrentUserChannelAdmin = () => {
    if (!members || members.length === 0) return false;
    
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (!storedUserData) return false;
      
      const parsedUserData = JSON.parse(storedUserData);
      const currentUserId = parsedUserData.user_id;
      
      // Check if current user has admin membership role in the channel
      const currentUserMember = members.find(member => member.user_id === currentUserId);
      return currentUserMember?.membership_role === 'admin';
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  };

  // Function to determine which buttons to show
  const getVisibleButtons = () => {
    const isApprover = isCurrentUserApprover();
    const isAdmin = isCurrentUserAdmin();
    const isChannelAdmin = isCurrentUserChannelAdmin();
    
    return {
      showApprove: isApprover,
      showReject: isApprover,
      showReassign: isApprover || isAdmin || isChannelAdmin
    };
  };

  // Determine if approver is already set from the approvals prop (from API)
  const hasExistingApprover = approvals && approvals.length > 0 && approvals[0]?.approval_details?.approver;

  // Fetch approval status and members when panel opens; use API only, never overwrite when approver already set in local state
  useEffect(() => {
    if (open && messageId) {
      if (hasExistingApprover && approvals && approvals.length > 0) {
        // Approver already set from API data – show second screen (Approve/Reject/Reassign + Activity Log)
        const approval = approvals[0];
        if (approval.approval_details.approver) {
          setApprovalStatus({
            id: approval.id,
            status: approval.approval_details.status as any,
            approver: {
              id: approval.approval_details.approver.id,
              name: approval.approval_details.approver.name,
              email: approval.approval_details.approver.email_id,
              avatar: approval.approval_details.approver.avatar_url || approval.approval_details.approver.name.charAt(0)
            },
            assignedBy: {
              id: approval.approval_details.requested_by.id,
              name: approval.approval_details.requested_by.name,
              email: approval.approval_details.requested_by.email_id,
              avatar: approval.approval_details.requested_by.avatar_url || approval.approval_details.requested_by.name.charAt(0)
            },
            assignedAt: approval.approval_details.created_at,
            message: approval.approval_details.description,
            actionPreview: approval.approval_details.action_preview ?? undefined
          });
        }
      } else if (!approvalsLoading) {
        // No approver in props: only fetch from API if we don't already have an approver in local state
        // (avoids overwriting right after user sets approver, before parent refetch completes)
        if (!approvalStatus?.approver) {
          fetchApprovalStatus();
        }
      }
      fetchMembers();
    }
  }, [open, messageId, threadId, hasExistingApprover, approvals, approvalsLoading]);

  const fetchApprovalStatus = async () => {
    if (!threadId || !messageId) return;
    try {
      setLoading(true);
      setErrorMessage(null);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.LIST}?thread_id=${threadId}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const list = Array.isArray(data.approvals) ? data.approvals : [];
      // Prefer approval that matches this message (by context.message_id if present), else first
      const forMessage = list.find(
        (a: any) => a.context?.message_id === messageId || a.approval_details?.approval_metadata?.message_id === messageId
      );
      const approval = forMessage ?? list[0];
      if (approval?.approval_details?.approver) {
        // API returned an approval with approver – show second screen
        setApprovalStatus({
          id: approval.id,
          status: approval.approval_details.status as any,
          approver: {
            id: approval.approval_details.approver.id,
            name: approval.approval_details.approver.name,
            email: approval.approval_details.approver.email_id,
            avatar: approval.approval_details.approver.avatar_url || approval.approval_details.approver.name.charAt(0)
          },
          assignedBy: {
            id: approval.approval_details.requested_by.id,
            name: approval.approval_details.requested_by.name,
            email: approval.approval_details.requested_by.email_id,
            avatar: approval.approval_details.requested_by.avatar_url || approval.approval_details.requested_by.name.charAt(0)
          },
          assignedAt: approval.approval_details.created_at,
          message: approval.approval_details.description,
          actionPreview: approval.approval_details.action_preview ?? undefined
        });
      } else {
        // No approver yet – minimal state so "Select Approver" form shows; assignedBy = current user
        let currentUser = { id: '', name: 'You', email: '', avatar: 'Y' };
        try {
          const stored = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
          if (stored) {
            const parsed = JSON.parse(stored);
            currentUser = {
              id: parsed.user_id || '',
              name: parsed.name || parsed.username || 'You',
              email: parsed.email_id || parsed.email || '',
              avatar: parsed.name?.charAt(0) || parsed.username?.charAt(0) || 'Y'
            };
          }
        } catch (_) {}
        setApprovalStatus({
          id: approval?.id || '',
          status: (approval?.approval_details?.status as any) || 'pending',
          assignedBy: approval?.approval_details?.requested_by
            ? {
                id: approval.approval_details.requested_by.id,
                name: approval.approval_details.requested_by.name,
                email: approval.approval_details.requested_by.email_id,
                avatar: approval.approval_details.requested_by.avatar_url || approval.approval_details.requested_by.name.charAt(0)
              }
            : currentUser,
          assignedAt: approval?.approval_details?.created_at || new Date().toISOString(),
          message: approval?.approval_details?.description || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch approval status:', error);
      setErrorMessage('Failed to load approval status');
      // Minimal state so user can still use "Select Approver"
      let currentUser = { id: '', name: 'You', email: '', avatar: 'Y' };
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (stored) {
          const parsed = JSON.parse(stored);
          currentUser = { id: parsed.user_id || '', name: parsed.name || 'You', email: parsed.email_id || '', avatar: parsed.name?.charAt(0) || 'Y' };
        }
      } catch (_) {}
      setApprovalStatus({
        id: '',
        status: 'pending',
        assignedBy: currentUser,
        assignedAt: new Date().toISOString(),
        message: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    if (!channelId) return;
    
    try {
      setMembersLoading(true);
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.LIST_MEMBERS(channelId)}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Handle the actual API response structure: { items: [...], total: number }
      const membersData = data.items || data.members || data || [];
      setMembers(Array.isArray(membersData) ? membersData : []);
      
    } catch (error) {
      console.error('Failed to fetch members:', error);
      // Fallback to mock data if API fails
      setMembers([
        { user_id: '1', name: 'Shay', email_id: 'shay@example.com', user_role: 'admin', membership_role: 'admin' },
        { user_id: '2', name: 'Ashok', email_id: 'ashok@example.com', user_role: 'user', membership_role: 'member' },
        { user_id: '3', name: 'John', email_id: 'john@example.com', user_role: 'user', membership_role: 'member' },
        { user_id: '4', name: 'Sarah', email_id: 'sarah@example.com', user_role: 'user', membership_role: 'member' },
      ]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all modes and form data so next open loads fresh from API
    setApprovalStatus(null);
    setSelectedAction(null);
    setComment('');
    setSelectedApprover('');
    setErrorMessage(null);
    onClose();
  };

  const handleActionClick = (action: 'approve' | 'reject' | 'reassign') => {
    setSelectedAction(action);
    setComment('');
    setSelectedApprover('');
  };

  const handleSaveAction = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      // Validation
      if (selectedAction === 'reassign' && !selectedApprover) {
        setErrorMessage('Please select an approver');
        return;
      }

      if (selectedAction === 'reject' && !comment.trim()) {
        setErrorMessage('Rejection reason is required');
        return;
      }

      if (!approvalStatus?.id) {
        setErrorMessage('Approval ID is required');
        return;
      }

      let response;
      let result;

      if (selectedAction === 'reassign') {
        // Use specific reassign endpoint for reassignment
        const requestBody = {
          new_approver_user_id: selectedApprover
        };

        response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.REASSIGN(approvalStatus.id)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)}`
          },
          body: JSON.stringify(requestBody)
        });
      } else {
        // Use generic action endpoint for approve/reject (same as ApprovalsPage.tsx)
        const requestBody: any = {
          action: selectedAction,
          comment: comment.trim() || undefined
        };

        // Add rejection_reason if action is reject
        if (selectedAction === 'reject') {
          requestBody.rejection_reason = comment.trim();
        }

        response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.ACTION(approvalStatus.id)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)}`
          },
          body: JSON.stringify(requestBody)
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      result = await response.json();

      // Update local state with the response
      if (approvalStatus) {
        setApprovalStatus({
          ...approvalStatus,
          status: selectedAction === 'approve' ? 'approved' : selectedAction === 'reject' ? 'rejected' : 'reassigned',
          message: comment.trim() || undefined
        });
      }

      
      // Reset form
      setSelectedAction(null);
      setComment('');
      setSelectedApprover('');
      
      // Notify parent component to refresh approvals
      if (onApprovalUpdated) {
        onApprovalUpdated();
      }
      
    } catch (error) {
      console.error(`Failed to ${selectedAction} approval:`, error);
      setErrorMessage(error instanceof Error ? error.message : `Failed to ${selectedAction} approval. Please try again.`);
      
      // Auto-remove error after 5 seconds
      setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleApproverSelect = async (approverId: string) => {
    if (!approverId || !messageId) return;

    try {
      setLoading(true);
      setErrorMessage(null);

      // Create new approval request
      const requestBody = {
        message_id: messageId,
        approver_user_id: approverId,
        thread_id: threadId || null, // Optional - will be auto-detected if not provided
        title: null, // Optional - auto-generated if not provided
        description: null // Optional - auto-generated if not provided
      };

      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.CREATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Check for specific error message about pending approval
        if (errorData.detail && errorData.detail.includes('already a pending approval')) {
          throw new Error(errorData.detail);
        }
        
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const selectedMember = members.find(member => member.user_id === approverId);
      // Current user as requested_by for Activity Log
      let requestedBy = approvalStatus?.assignedBy ?? { id: '', name: 'You', email: '', avatar: 'Y' };
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (stored) {
          const parsed = JSON.parse(stored);
          requestedBy = {
            id: parsed.user_id || '',
            name: parsed.name || parsed.username || 'You',
            email: parsed.email_id || parsed.email || '',
            avatar: parsed.name?.charAt(0) || parsed.username?.charAt(0) || 'Y'
          };
        }
      } catch (_) {}
      // Update local state immediately so second screen (Approve/Reject/Reassign + Activity Log) shows
      setApprovalStatus({
        id: result.id,
        status: 'pending',
        approver: {
          id: approverId,
          name: selectedMember?.name || 'Unknown',
          email: selectedMember?.email_id || 'unknown@example.com',
          avatar: selectedMember?.name?.charAt(0) || 'U'
        },
        assignedBy: result.approval_details?.requested_by
          ? {
              id: result.approval_details.requested_by.id,
              name: result.approval_details.requested_by.name,
              email: result.approval_details.requested_by.email_id || '',
              avatar: result.approval_details.requested_by.avatar_url || result.approval_details.requested_by.name?.charAt(0) || 'U'
            }
          : requestedBy,
        assignedAt: result.approval_details?.created_at || new Date().toISOString(),
        message: approvalStatus?.message || result.approval_details?.description || ''
      });

      // Clear the selected approver
      setSelectedApprover('');
      // Notify parent to refetch approvals so props stay in sync
      if (onApprovalUpdated) {
        onApprovalUpdated();
      }
      
    } catch (error) {
      console.error('Failed to create approval request:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create approval request. Please try again.');
      
      // Auto-remove error after 5 seconds and clear selected approver to allow retry
      setTimeout(() => {
        setErrorMessage(null);
        setSelectedApprover(''); // Clear selection to allow retry of same user
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAction = () => {
    setSelectedAction(null);
    setComment('');
    setSelectedApprover('');
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours === 1) {
      return '1 hr ago';
    } else if (diffInHours < 24) {
      return `${diffInHours} hrs ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
  };

     if (!open) return null;

   return (
     <>
       {/* Backdrop */}
       <Box
         sx={{
           position: 'fixed',
           top: 0,
           left: 0,
           right: 0,
           bottom: 0,
           backgroundColor: 'rgba(0, 0, 0, 0.3)',
           zIndex: 999998,
           opacity: open ? 1 : 0,
           transition: 'opacity 0.3s ease-in-out',
           pointerEvents: open ? 'auto' : 'none',
         }}
         onClick={handleClose}
       />
       {/* Panel - theme-aware background and border for dark mode */}
         <Box
      sx={{
         position: 'fixed',
         top: 0,
         right: 0,
         width: '350px',
         height: '100vh',
         backgroundColor: theme.palette.background.paper,
         borderLeft: `1px solid ${theme.palette.divider}`,
         boxShadow: isDark ? '-2px 0 8px rgba(0,0,0,0.4)' : '-2px 0 8px rgba(0,0,0,0.1)',
         display: 'flex',
         flexDirection: 'column',
         zIndex: 999999,
         transform: open ? 'translateX(0)' : 'translateX(100%)',
         transition: 'transform 0.3s ease-in-out',
         overflow: 'hidden',
       }}
     >
      {/* Panel Header - theme-aware */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Get Approval
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

             {/* Panel Content */}
       <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
         {loading || approvalsLoading ? (
           <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '200px', gap: 2 }}>
             <CircularProgress size={32} />
             <Typography color="text.secondary">
               {approvalsLoading ? 'Loading approval data...' : 'Loading approval status...'}
             </Typography>
           </Box>
         ) : approvalStatus ? (
           <>
             {/* Error Message - Show above content when there's an error */}
             {errorMessage && (
               <Box sx={{ 
                 mb: 2, 
                 p: 2, 
                 backgroundColor: isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee', 
                 border: `1px solid ${theme.palette.error.main}`, 
                 borderRadius: 1 
               }}>
                 <Typography sx={{ color: theme.palette.error.main, fontSize: '14px' }}>
                   {errorMessage}
                 </Typography>
               </Box>
             )}
             
             {/* Check if approver is already assigned */}
             {approvalStatus.approver ? (
               // Approver is assigned - show action buttons and activity log
               <>
                 {/* What approving will run — shown before the buttons so the approver reads it first.
                     Once settled it is a record, so the heading must not read as a pending decision. */}
                 <ApprovalActionPreview
                   actions={approvalStatus.actionPreview}
                   heading={previewHeadingFor(approvalStatus.status)}
                 />

                 {/* Action Buttons */}
                 <Box sx={{ 
                   display: 'flex', 
                   gap: 0.5, 
                   mb: 3,
                   flexWrap: 'nowrap',
                   justifyContent: 'flex-start'
                 }}>
                   {/* Get button visibility based on user permissions */}
                   {(() => {
                     const { showApprove, showReject, showReassign } = getVisibleButtons();
                     
                     return (
                       <>
                         {/* Only show Approve and Reject buttons when status is pending AND user is approver */}
                         {approvalStatus.status === 'pending' && showApprove && (
                           <Button
                             size="small"
                             variant={selectedAction === 'approve' ? 'contained' : 'outlined'}
                             startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                             onClick={() => handleActionClick('approve')}
                             disabled={loading}
                             sx={{
                               backgroundColor: selectedAction === 'approve' ? '#4caf50' : 'transparent',
                               borderColor: '#4caf50',
                               color: selectedAction === 'approve' ? 'white' : '#4caf50',
                               fontSize: '11px',
                               padding: '2px 8px',
                               minWidth: 'auto',
                               height: '28px',
                               '&:hover': {
                                 backgroundColor: selectedAction === 'approve' ? '#45a049' : 'rgba(76, 175, 80, 0.04)'
                               },
                               '&:disabled': {
                                 backgroundColor: '#ccc',
                                 color: '#fff',
                                 borderColor: '#ccc'
                               }
                             }}
                           >
                             Approve
                           </Button>
                         )}
                         
                         {approvalStatus.status === 'pending' && showReject && (
                           <Button
                             size="small"
                             variant={selectedAction === 'reject' ? 'contained' : 'outlined'}
                             startIcon={<XIcon sx={{ fontSize: 14 }} />}
                             onClick={() => handleActionClick('reject')}
                             disabled={loading}
                             sx={{
                               backgroundColor: selectedAction === 'reject' ? '#f44336' : 'transparent',
                               borderColor: '#f44336',
                               color: selectedAction === 'reject' ? 'white' : '#f44336',
                               fontSize: '11px',
                               padding: '2px 8px',
                               minWidth: 'auto',
                               height: '28px',
                               '&:hover': {
                                 backgroundColor: selectedAction === 'reject' ? '#d32f2f' : 'rgba(244, 67, 54, 0.04)'
                               },
                               '&:disabled': {
                                 backgroundColor: '#ccc',
                                 color: '#fff',
                                 borderColor: '#ccc'
                               }
                             }}
                           >
                             Reject
                           </Button>
                         )}
                         
                         {/* Show Reassign button if user has permission */}
                         {showReassign && (
                           <Button
                             size="small"
                             variant={selectedAction === 'reassign' ? 'contained' : 'outlined'}
                             startIcon={<ReassignIcon sx={{ fontSize: 14 }} />}
                             onClick={() => handleActionClick('reassign')}
                             disabled={loading}
                             sx={{
                               backgroundColor: selectedAction === 'reassign' ? '#ff9800' : 'transparent',
                               borderColor: '#ff9800',
                               color: selectedAction === 'reassign' ? 'white' : '#ff9800',
                               fontSize: '11px',
                               padding: '2px 8px',
                               minWidth: 'auto',
                               height: '28px',
                               '&:hover': {
                                 backgroundColor: selectedAction === 'reassign' ? '#f57c00' : 'rgba(255, 152, 0, 0.04)'
                               },
                               '&:disabled': {
                                 backgroundColor: '#ccc',
                                 color: '#fff',
                                 borderColor: '#ccc'
                               }
                             }}
                           >
                             Reassign
                           </Button>
                         )}
                       </>
                     );
                   })()}
                 </Box>

                 {/* Action Form */}
                 {selectedAction && (
                   <>
                     {selectedAction === 'reassign' ? (
                                               // Reassign form - show approver dropdown
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ width: '100%', mb: 2 }}>
                            <select
                              value={selectedApprover}
                              onChange={(e) => setSelectedApprover(e.target.value)}
                              disabled={membersLoading}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '14px',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '4px',
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                cursor: 'pointer',
                                height: '38px'
                              }}
                            >
                              {membersLoading ? (
                                <option disabled>Loading members...</option>
                              ) : (
                                <>
                                  <option value="" disabled>
                                    Select Approver *
                                  </option>
                                  {members.map((member) => (
                                    <option key={member.user_id} value={member.user_id}>
                                      {member.name} ({member.email_id})
                                    </option>
                                  ))}
                                </>
                              )}
                            </select>
                          </Box>
                        
                         {/* Action Buttons */}
                         <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                           <Button
                             size="small"
                             variant="outlined"
                             onClick={handleCancelAction}
                             disabled={loading}
                             sx={{ 
                               fontSize: '11px', 
                               padding: '2px 8px', 
                               height: '28px',
                               borderColor: 'divider',
                               color: 'text.secondary'
                             }}
                           >
                             Cancel
                           </Button>
                           <Button
                             size="small"
                             variant="contained"
                             onClick={handleSaveAction}
                             disabled={loading}
                             sx={{ 
                               fontSize: '11px', 
                               padding: '2px 8px', 
                               height: '28px',
                               backgroundColor: '#ff9800',
                               '&:hover': {
                                 backgroundColor: '#f57c00'
                               }
                             }}
                           >
                             Save
                           </Button>
                         </Box>
                       </Box>
                     ) : (
                       // Approve/Reject form - show comment box
                       <Box sx={{ mt: 2 }}>
                         <TextField
                           fullWidth
                           multiline
                           rows={3}
                           placeholder={selectedAction === 'reject' ? "Rejection reason (required)" : "Add a comment (optional)"}
                           value={comment}
                           onChange={(e) => setComment(e.target.value)}
                           size="small"
                           required={selectedAction === 'reject'}
                           error={selectedAction === 'reject' && !comment.trim()}
                           helperText={selectedAction === 'reject' && !comment.trim() ? "Rejection reason is required" : ""}
                           sx={{ mb: 2 }}
                         />
                        
                         {/* Action Buttons */}
                         <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                           <Button
                             size="small"
                             variant="outlined"
                             onClick={handleCancelAction}
                             disabled={loading}
                             sx={{ 
                               fontSize: '11px', 
                               padding: '2px 8px', 
                               height: '28px',
                               borderColor: 'divider',
                               color: 'text.secondary'
                             }}
                           >
                             Cancel
                           </Button>
                           <Button
                             size="small"
                             variant="contained"
                             onClick={handleSaveAction}
                             disabled={loading}
                             sx={{ 
                               fontSize: '11px', 
                               padding: '2px 8px', 
                               height: '28px',
                               backgroundColor: '#1976d2',
                               '&:hover': {
                                 backgroundColor: '#1565c0'
                               }
                             }}
                           >
                             Save
                           </Button>
                         </Box>
                       </Box>
                     )}
                   </>
                 )}

                 {/* Activity Log */}
                 <Box sx={{ mt: 2 }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
                     Activity Log
                   </Typography>
                   
                   {/* Display approval history from API data */}
                   {approvals && approvals.length > 0 && approvals[0]?.approval_history && approvals[0].approval_history.length > 0 ? (
                     approvals[0].approval_history.map((historyItem, index) => (
                       <Box key={index} sx={{ 
                         display: 'flex', 
                         alignItems: 'flex-start', 
                         gap: 2, 
                         mb: index < approvals[0].approval_history.length - 1 ? 2 : 0,
                         pb: index < approvals[0].approval_history.length - 1 ? 2 : 0,
                         borderBottom: index < approvals[0].approval_history.length - 1 ? '1px solid #f0f0f0' : 'none'
                       }}>
                         <Avatar 
                           sx={{ 
                             width: 32, 
                             height: 32, 
                             bgcolor: '#00838f',
                             fontSize: '14px',
                             fontWeight: 'bold'
                           }}
                         >
                           {historyItem.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                         </Avatar>
                         
                         <Box sx={{ flex: 1 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                             <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                               {historyItem.user?.name || 'Unknown User'}
                             </Typography>
                             <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                               |
                             </Typography>
                             <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                               {formatRelativeTime(historyItem.timestamp)}
                             </Typography>
                           </Box>
                           
                           <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                             {/* API sometimes returns "Name@Name" in details; collapse to single name */}
                             {historyItem.details?.replace(/([^@]+)@\1/g, '$1') || ''}
                           </Typography>
                         </Box>
                       </Box>
                     ))
                   ) : (
                     /* Fallback to static activity log if no history data available */
                     <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                       <Avatar 
                         sx={{ 
                           width: 32, 
                           height: 32, 
                           bgcolor: '#00838f',
                           fontSize: '14px',
                           fontWeight: 'bold'
                         }}
                       >
                         {approvalStatus.assignedBy.avatar}
                       </Avatar>
                       
                       <Box sx={{ flex: 1 }}>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                           <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                             {approvalStatus.assignedBy.name}
                           </Typography>
                           <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                             |
                           </Typography>
                           <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                             {formatRelativeTime(approvalStatus.assignedAt)}
                           </Typography>
                         </Box>
                         
                         <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                           Assigned the approval to{' '}
                           <Box component="span" sx={{ fontWeight: 'bold' }}>
                             {approvalStatus.approver.name}
                           </Box>{' '}
                           and set the status to{' '}
                           <Box component="span" sx={{ fontWeight: 'bold' }}>
                             Pending Approval
                           </Box>
                         </Typography>
                       </Box>
                     </Box>
                   )}
                 </Box>
               </>
                            ) : (
                                                // No approver assigned yet - show approver selection form
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ width: '100%' }}>
                                         <select
                       value={selectedApprover}
                       onChange={(e) => {
                         const approverId = e.target.value;
                         setSelectedApprover(approverId);
                         // Always call the API, even if selecting the same value
                         if (approverId) {
                           handleApproverSelect(approverId);
                         }
                       }}
                       disabled={membersLoading}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '4px',
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        cursor: 'pointer',
                        height: '38px'
                      }}
                    >
                      {membersLoading ? (
                        <option disabled>Loading members...</option>
                      ) : (
                        <>
                          <option value="">
                            Select Approver
                          </option>
                          {members.map((member) => (
                            <option key={member.user_id} value={member.user_id}>
                              {member.name} ({member.email_id})
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </Box>
                </Box>
             )}
                       </>
         ) : (
           <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
             <Typography color="text.secondary">
               No approval data available
             </Typography>
           </Box>
         )}
       </Box>
     </Box>
     </>
   );
 };

export default ApprovalPanel;